import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StringHelper } from 'src/app/shared/string-helper';
import { DateHelper } from 'src/app/core/date/date-helper';
import {
  DataCountCardDataSource,
  DataCountCardRequest,
  DataCountCardResponse,
  DEFAULT_DATA_COUNT_THRESHOLD,
} from './data-count-card.models';

@Component({
  selector: 'app-data-count-card',
  templateUrl: './data-count-card.component.html',
  styleUrls: ['./data-count-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataCountCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string;
  @Input() staffId: number;
  @Input() typeId: number;
  // `filters` and `dataSource` are intentionally opaque: this component is shared across consumers
  // whose filter shapes differ (e.g. plain strings, dictionary objects). Each consumer narrows
  // its own shape via DataCountCardDataSource<TFilters> on its concrete service implementation.
  @Input() filters?: unknown;
  @Input() filterString?: string | null;
  @Input() dataSource: DataCountCardDataSource<unknown>;
  @Input() enableViewData = false;
  @Input() maxThreshold = DEFAULT_DATA_COUNT_THRESHOLD;

  loading = true;
  error = false;
  errorMessage?: string;
  dataCount = 0;
  tooltip = 'Export will be sent to your email address.';
  exportEnabled = true;
  reportLink: string | null = null;
  dynamicDate = false;
  days = 0;

  private intersectionObserver?: IntersectionObserver;
  private hasLoadedData = false;
  private isVisible = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly stringHelper: StringHelper,
    private readonly elementRef: ElementRef,
    private readonly dateHelper: DateHelper
  ) {}

  ngOnInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['staffId'] || changes['typeId'] || changes['filters'] || changes['filterString']) {
      // Only reload if we've already loaded data and component is visible
      if (this.hasLoadedData && this.isVisible) {
        this.loadDataCount();
      }
    }
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  retry(): void {
    this.loadDataCount();
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (entry.isIntersecting && !this.hasLoadedData) {
            this.loadDataCount();
            this.hasLoadedData = true;
          }
        });
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }

  exportData(): void {
    if (!this.dataSource) {
      return;
    }

    this.dataSource.export(this.buildRequest()).subscribe({
      next: () => {
        this.snackBar.open(
          "Your request for data has been added to the queue. If you haven't received your data in a reasonable amount of time please contact system support.",
          'Close',
          {}
        );
      },
      // RxJS error callbacks accept arbitrary thrown values (HttpErrorResponse, string, Error, etc.)
      // so `unknown` is the honest type here.
      error: (error: unknown) => {
        this.snackBar.open(String(error ?? 'Unable to export data.'), 'Close', {});
      },
    });
  }

  viewData(): void {
    if (!this.enableViewData) {
      return;
    }

    if (!this.reportLink) {
      this.snackBar.open('No data available to view.', 'Close', {});
      return;
    }

    const request = this.buildRequest();
    const filters: string[] = [];

    const rawFilter = request.filterString ?? this.asString(request.filters);
    if (rawFilter && this.stringHelper.notNullOrEmpty(rawFilter)) {
      const formattedFilter = rawFilter
        .split(';')
        .filter(Boolean)
        .map((pair) => {
          const [key, value] = pair.split('::');
          if (!key) {
            return '';
          }
          const baseKey = key.replace(/Id$/, '');
          const capitalizedKey = baseKey.charAt(0).toUpperCase() + baseKey.slice(1);
          return `selected${capitalizedKey}::${value ?? ''}`;
        })
        .filter(Boolean)
        .join(';');

      if (formattedFilter) {
        filters.push(formattedFilter);
      }
    }

    if (this.dynamicDate) {
      // URL filter format uses commas inside each date because '-' separates start/end.
      const toCommaDate = (date: Date): string => this.dateHelper.formatDateOnly(date).replace(/-/g, ',');

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - this.days);
      const endDate = new Date();
      const formattedDateFilter = `dateSelected::${toCommaDate(startDate)}-${toCommaDate(endDate)};`;
      filters.unshift(formattedDateFilter);
    }

    const targetUrl = filters.length > 0 ? `${this.reportLink}?extraFilter=${filters.join('')}` : this.reportLink;

    window.open(targetUrl, '_blank');
  }

  private loadDataCount(): void {
    if (!this.dataSource || !this.staffId || !this.typeId) {
      return;
    }

    this.loading = true;
    this.error = false;
    this.exportEnabled = true;
    this.tooltip = 'Export will be sent to your email address.';

    this.dataSource
      .load(this.buildRequest())
      .pipe(catchError((error: unknown) => this.handleError(error)))
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.applyResponse(response);
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  // Returns DataCountCardRequest with the default unknown TFilters; each consumer's data source
  // narrows the parameter when implementing DataCountCardDataSource<TFilters>.
  private buildRequest(): DataCountCardRequest {
    const filterString = this.filterString ?? (typeof this.filters === 'string' ? this.filters : null);
    return {
      staffId: this.staffId,
      typeId: this.typeId,
      filters: this.filters,
      filterString,
    };
  }

  private applyResponse(response: DataCountCardResponse): void {
    this.dataCount = response.count ?? 0;
    this.reportLink = response.reportLink ?? null;
    this.dynamicDate = response.dynamicDate ?? false;
    this.days = response.days ?? 0;

    if (this.dataCount === 0) {
      this.tooltip = 'No data to export.';
      this.exportEnabled = false;
    } else if (this.dataCount >= this.maxThreshold) {
      this.tooltip = 'Data count exceeds the maximum limit of 50,000. Please refine your search criteria.';
      this.exportEnabled = false;
    }
  }

  // RxJS catchError can deliver any thrown value -- string, Error, HttpErrorResponse;
  // String() coerces all of them to a displayable form for the user.
  private handleError(error: unknown): Observable<null> {
    this.errorMessage = String(error ?? 'Unable to load data.');
    this.error = true;
    this.loading = false;
    this.cdr.detectChanges();
    return of(null);
  }

  // `value` is intentionally typed as the opaque `filters` input shape; this helper narrows to a string
  // when filters happens to be passed as a raw filter string instead of a structured object.
  private asString(value: unknown): string | null {
    if (typeof value === 'string') {
      return value;
    }

    return null;
  }
}
